import { useCallback, useEffect, useRef } from 'react'
import type { CardStatus } from '../../../components/InterestCard'
import type { OneShotTipDismissReason } from '../../../components/OneShotTip'
import { retireRationedTip } from '../../../lib/nuxTips'
import type { PartialRangeEntryPoint } from '../../../lib/analytics'
import type { Set as FestivalSet, PartialRange } from '../../../types'
import { SLOT_PX, timeToMinutes } from './timeUtils'
import { useSetTrim } from './useSetTrim'
import { useReportWhenVisible } from './useTrimConflictTip'
import { useDayCutoff } from '../../../contexts/DayCutoffContext'

// How much of the card to hide for a block of this many minutes.
function compactForDuration(min: number): 'xs' | 'sm' | 'md' | undefined {
  return min < 20 ? 'xs' : min < 35 ? 'sm' : min < 50 ? 'md' : undefined
}

// Lines of name a compact block has room for (see `cardNameClass`): its height
// less margin, border and padding, over its capped line height — sm at
// text-xs's 15px; md at text-sm's 17.5px, keeping a line for the time.
function nameLinesForDuration(min: number, compact: 'xs' | 'sm' | 'md' | undefined): number | undefined {
  const height = min * SLOT_PX
  if (compact === 'sm') return Math.max(1, Math.floor((height - 10) / 15))
  if (compact === 'md') return Math.max(1, Math.floor((height - 32) / 17.5))
  return undefined
}

/** A schedule block's status, the viewer's saved/draft range window, and the long-press trim state. */
export function useSetBlockModel({
  set,
  registerBlock,
  interestedSetIds,
  goingSetIds,
  ownPartialRange,
  onUpdateOwnRange,
  clashesWith,
  onClashVisible,
  showClashTip,
  onDismissClashTip,
}: {
  set: FestivalSet
  registerBlock?: (id: string, el: HTMLDivElement | null) => void
  interestedSetIds: Set<string>
  goingSetIds: Set<string>
  ownPartialRange?: PartialRange | null
  onUpdateOwnRange?: (setId: string, partialRange: PartialRange | null, entryPoint?: PartialRangeEntryPoint) => void
  clashesWith?: string
  onClashVisible?: (setId: string) => void
  showClashTip: boolean
  onDismissClashTip?: (via: OneShotTipDismissReason) => void
}) {
  const cutoff = useDayCutoff()
  // Stable ref callback (identity depends only on set.id + the stable
  // registerBlock) so it doesn't defeat the React.memo bail-out.
  const cardEl = useRef<HTMLDivElement | null>(null)
  const setBlockRef = useCallback(
    (el: HTMLDivElement | null) => {
      cardEl.current = el
      registerBlock?.(set.id, el)
    },
    [registerBlock, set.id],
  )
  const trimOverlayEl = useRef<HTMLDivElement | null>(null)
  const trimBoxEl = useRef<HTMLDivElement | null>(null)

  const status: CardStatus = goingSetIds.has(set.id)
    ? 'going'
    : interestedSetIds.has(set.id)
      ? 'interested'
      : 'none'
  const durationMin = timeToMinutes(set.endTime, cutoff) - timeToMinutes(set.startTime, cutoff)
  // Only the viewer's own range reshapes the card: the slice they'll catch stays
  // in color over a grayscale rest of the set. The layout stays the full card's,
  // so a short range doesn't cost the facepile.
  const range = status !== 'none' && ownPartialRange && durationMin > 0
    ? {
        start: Math.max(0, Math.min(ownPartialRange.startMin, durationMin)),
        end: Math.max(0, Math.min(ownPartialRange.endMin, durationMin)),
      }
    : null
  const savedPartial = range && range.end > range.start && (range.start > 0 || range.end < durationMin) ? range : null
  const canTrim = status !== 'none' && !!onUpdateOwnRange && durationMin > 0
  const trim = useSetTrim({
    measureRef: trimBoxEl,
    withinRefs: [cardEl, trimOverlayEl],
    duration: durationMin,
    range: savedPartial ? { startMin: savedPartial.start, endMin: savedPartial.end } : null,
    onCommit: r => onUpdateOwnRange?.(set.id, r, 'schedule_drag'),
  })
  const { draft: trimDraft, stop: stopTrim } = trim
  const trimming = trimDraft !== null
  useEffect(() => {
    if (trimming && !canTrim) stopTrim()
  }, [trimming, canTrim, stopTrim])
  const reportClash = useCallback(() => onClashVisible?.(set.id), [onClashVisible, set.id])
  useReportWhenVisible(cardEl, clashesWith && canTrim && onClashVisible ? reportClash : undefined)
  const startTrim = () => {
    // Found long-press on their own — the clash tip has nothing left to teach.
    retireRationedTip('set_trim_conflict')
    if (showClashTip) onDismissClashTip?.('tap')
    trim.start()
  }
  // While trimming the window is always drawn, even at full length, so the
  // grips sit on the same border whether or not a range is saved yet.
  const partial = trimDraft ? { start: trimDraft[0], end: trimDraft[1] } : savedPartial

  return {
    cardEl,
    setBlockRef,
    trimOverlayEl,
    trimBoxEl,
    status,
    durationMin,
    canTrim,
    trim,
    trimming,
    stopTrim,
    startTrim,
    partial,
    compact: compactForDuration(durationMin),
    nameLines: nameLinesForDuration(durationMin, compactForDuration(durationMin)),
  }
}
